import { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { collection, doc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ENABLE_NATIVE_MOBILE_PUSH } from '../features/app/appConfig';
import {
  buildActiveNotifications,
  buildReadNotificationsSnapshot,
  countActiveNotifications,
  getNotificationKey,
  normalizeNotificationPreferences,
} from '../features/app/appNotificationHelpers';
import {
  refreshPermissionStatusFlow,
  requestAppPermissionsFlow,
} from '../features/app/appPermissionHelpers';
import {
  getFCMToken,
  onForegroundMessage,
  requestNotificationPermission,
  sendBrowserNotification,
} from '../utils/notifications';

export default function useAppNotifications({
  approved,
  bijzonderheden = [],
  garanties = [],
  magazijnVoorraad = [],
  notificationPreferences = {},
  userProfile = {},
  notificationsEnabled,
  onIncomingNotification,
  onderdelen = [],
  showToast,
  user,
}) {
  const [gelezenNotificaties, setGelezenNotificaties] = useState(() => {
    try {
      const saved = localStorage.getItem('gelezenNotificaties');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [ticketNotifications, setTicketNotifications] = useState([]);
  const [permissionStatus, setPermissionStatus] = useState({
    notifications: 'unknown',
    geolocation: 'unknown',
    microphone: 'unknown',
  });
  const [requestingPermissions, setRequestingPermissions] = useState(false);
  const [permissionsPromptDismissed, setPermissionsPromptDismissed] = useState(false);
  const foregroundMessageUnsubscribeRef = useRef(null);
  const nativePushListenerHandlesRef = useRef([]);

  const clearNativePushListeners = async () => {
    if (!nativePushListenerHandlesRef.current.length) return;

    await Promise.all(
      nativePushListenerHandlesRef.current.map(async (handle) => {
        try {
          if (handle && typeof handle.remove === 'function') {
            await handle.remove();
          }
        } catch {
          void 0;
        }
      })
    );

    nativePushListenerHandlesRef.current = [];
  };

  const initializeNativeMobilePush = async () => {
    const nativePlatform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
    const isNativeMobile = nativePlatform === 'android' || nativePlatform === 'ios';
    if (!isNativeMobile) return false;
    if (!ENABLE_NATIVE_MOBILE_PUSH) {
      console.log('ℹ️ Native mobile push is disabled (feature flag)');
      return false;
    }

    const toNotificationState = (receiveValue) => {
      if (receiveValue === 'granted') return 'granted';
      if (receiveValue === 'denied') return 'denied';
      return 'default';
    };

    if (!PushNotifications || typeof PushNotifications.checkPermissions !== 'function') {
      console.log('ℹ️ Native push plugin not available at runtime');
      return false;
    }

    const permissionCheck = await PushNotifications.checkPermissions().catch(() => ({ receive: 'prompt' }));
    let receive = permissionCheck?.receive || 'prompt';

    if (receive === 'prompt') {
      const permissionRequest = await PushNotifications.requestPermissions().catch(() => ({ receive: 'denied' }));
      receive = permissionRequest?.receive || 'denied';
    }

    setPermissionStatus((prev) => ({
      ...prev,
      notifications: toNotificationState(receive),
    }));

    if (receive !== 'granted') {
      return false;
    }

    await clearNativePushListeners();

    nativePushListenerHandlesRef.current.push(
      await PushNotifications.addListener('registration', async (token) => {
        try {
          if (!user?.uid) return;

          let resolvedToken = token?.value;
          if (nativePlatform === 'ios') {
            const storedToken = await Preferences.get({ key: 'native_fcm_token' }).catch(() => ({ value: '' }));
            resolvedToken = storedToken?.value || token?.value;
          }

          await setDoc(doc(db, 'users', user.uid), {
            fcmToken: resolvedToken,
            fcmTokenUpdated: serverTimestamp(),
            notificationPreferences: normalizeNotificationPreferences(notificationPreferences),
            nativePlatform,
          }, { merge: true });
          console.log(`✅ Native ${nativePlatform} push token saved`);
        } catch (error) {
          console.warn('Native token save failed:', error);
        }
      })
    );

    nativePushListenerHandlesRef.current.push(
      await PushNotifications.addListener('registrationError', (error) => {
        console.error('Native push registration error:', error);
      })
    );

    nativePushListenerHandlesRef.current.push(
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        if (typeof onIncomingNotification === 'function') {
          onIncomingNotification(notification, notification?.data || {});
        }
      })
    );

    nativePushListenerHandlesRef.current.push(
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const notification = action?.notification || {};
        if (typeof onIncomingNotification === 'function') {
          onIncomingNotification(notification, notification?.data || {});
        }
      })
    );

    try {
      await PushNotifications.register();

      if (nativePlatform === 'ios' && typeof window !== 'undefined') {
        window.setTimeout(async () => {
          try {
            if (!user?.uid) return;
            const storedToken = await Preferences.get({ key: 'native_fcm_token' }).catch(() => ({ value: '' }));
            if (!storedToken?.value) return;
            await setDoc(doc(db, 'users', user.uid), {
              fcmToken: storedToken.value,
              fcmTokenUpdated: serverTimestamp(),
              notificationPreferences: normalizeNotificationPreferences(notificationPreferences),
              nativePlatform,
            }, { merge: true });
          } catch (syncError) {
            console.warn('Native iOS FCM token sync failed:', syncError);
          }
        }, 1500);
      }
    } catch (error) {
      console.warn('Native push register failed:', error);
      return false;
    }

    return true;
  };

  const initializeNotifications = async () => {
    try {
      const nativePlatform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
      const isNativeMobile = nativePlatform === 'android' || nativePlatform === 'ios';
      if (isNativeMobile) {
        await requestNotificationPermission();
        await initializeNativeMobilePush();
        return;
      }

      if (!('serviceWorker' in navigator)) {
        console.log('ℹ️ Service Worker not supported');
        return;
      }

      let hasPermission = typeof Notification !== 'undefined' && Notification.permission === 'granted';

      if (!hasPermission && typeof Notification !== 'undefined' && Notification.permission === 'default') {
        hasPermission = await requestNotificationPermission();
      }

      if (hasPermission && user?.uid) {
        const token = await getFCMToken();

        if (token) {
          await setDoc(doc(db, 'users', user.uid), {
            fcmToken: token,
            fcmTokenUpdated: serverTimestamp(),
            notificationPreferences: normalizeNotificationPreferences(notificationPreferences),
          }, { merge: true });

          console.log('✅ FCM token saved');
        }

        if (foregroundMessageUnsubscribeRef.current) {
          foregroundMessageUnsubscribeRef.current();
          foregroundMessageUnsubscribeRef.current = null;
        }

        foregroundMessageUnsubscribeRef.current = onForegroundMessage((payload) => {
          const { notification, data } = payload || {};
          if (typeof onIncomingNotification === 'function') {
            onIncomingNotification(notification, data || {});
          }
        });
      }
    } catch (error) {
      console.log('ℹ️ Notifications not available:', error.message);
    }
  };

  const refreshPermissionStatus = async () => {
    await refreshPermissionStatusFlow(setPermissionStatus);
  };

  const requestAppPermissions = async () => {
    await requestAppPermissionsFlow({
      requestingPermissions,
      setRequestingPermissions,
      notificationsEnabled,
      initializeNotifications,
      initializeNativeMobilePush,
      refreshPermissionStatus,
      showToast,
    });
  };

  const sendTestNotification = async () => {
    if (!user?.uid) {
      showToast('⚠️ Geen gebruiker beschikbaar voor notificatietest', 'warning');
      return;
    }

    try {
      const response = await fetch('/api/send-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          title: 'Test Notificatie',
          body: 'Dit is een test melding van Krinkels Robot Beheer',
          url: '/status',
        }),
      });

      if (!response.ok) {
        await sendBrowserNotification(
          'Test Notificatie',
          'Dit is een test melding van Krinkels Robot Beheer',
          '/krinkels-icon-192.png',
          { tag: 'infra-status-test' }
        );
      }

      showToast('✅ Notificatietest uitgevoerd', 'success');
    } catch (error) {
      console.error('sendTestNotification failed:', error);
      await sendBrowserNotification(
        'Test Notificatie (Local)',
        'Push server niet beschikbaar - lokale notificatie getoond',
        '/krinkels-icon-192.png',
        { tag: 'infra-status-test-local' }
      );
      showToast('⚠️ Push server niet beschikbaar, lokale notificatie gebruikt', 'warning');
    }
  };

  useEffect(() => {
    if (user && approved) {
      void refreshPermissionStatus();
      const dismissed = Boolean(userProfile?.permissionsPromptDismissedAt)
        || localStorage.getItem(`permissionsPromptDismissed:${user.uid}`) === '1';
      setPermissionsPromptDismissed(dismissed);
    }
  }, [approved, user, userProfile?.permissionsPromptDismissedAt]);

  useEffect(() => {
    if (user && approved && permissionStatus.notifications === 'granted' && notificationsEnabled) {
      void initializeNotifications();
    }
  }, [approved, notificationsEnabled, notificationPreferences, permissionStatus.notifications, user]);

  useEffect(() => () => {
    if (foregroundMessageUnsubscribeRef.current) {
      foregroundMessageUnsubscribeRef.current();
      foregroundMessageUnsubscribeRef.current = null;
    }
    void clearNativePushListeners();
  }, []);

  const isNotificationRead = (type, id) => {
    const key = getNotificationKey(type, id);
    return gelezenNotificaties[key] || false;
  };

  const markNotificationAsRead = (type, id) => {
    const key = getNotificationKey(type, id);
    const nextReadState = { ...gelezenNotificaties, [key]: true };
    setGelezenNotificaties(nextReadState);
    localStorage.setItem('gelezenNotificaties', JSON.stringify(nextReadState));
  };

  const markAllNotificationsAsRead = () => {
    const nextReadState = buildReadNotificationsSnapshot({
      onderdelen,
      magazijnVoorraad,
      bijzonderheden,
      garanties,
      tickets: ticketNotifications,
    });

    setGelezenNotificaties(nextReadState);
    localStorage.setItem('gelezenNotificaties', JSON.stringify(nextReadState));
  };

  const notificationCount = useMemo(() => countActiveNotifications({
    onderdelen,
    magazijnVoorraad,
    bijzonderheden,
    garanties,
    tickets: ticketNotifications,
    isRead: isNotificationRead,
  }), [bijzonderheden, garanties, gelezenNotificaties, magazijnVoorraad, onderdelen, ticketNotifications]);

  const notifications = useMemo(() => buildActiveNotifications({
    onderdelen,
    magazijnVoorraad,
    bijzonderheden,
    garanties,
    tickets: ticketNotifications,
    isRead: isNotificationRead,
  }), [bijzonderheden, garanties, gelezenNotificaties, magazijnVoorraad, onderdelen, ticketNotifications]);

  useEffect(() => {
    if (!user || !approved) { setTicketNotifications([]); return undefined; }
    let active = true;
    getDocs(query(collection(db, 'tickets'), orderBy('createdAt', 'desc'), limit(10)))
      .then((snapshot) => active && setTicketNotifications(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }))))
      .catch(() => active && setTicketNotifications([]));
    return () => { active = false; };
  }, [approved, user]);

  const dismissPermissionsPrompt = () => {
    if (user?.uid) {
      localStorage.setItem(`permissionsPromptDismissed:${user.uid}`, '1');
      void setDoc(doc(db, 'users', user.uid), {
        permissionsPromptDismissedAt: serverTimestamp(),
      }, { merge: true }).catch((error) => {
        console.warn('Permission prompt state could not be synchronized:', error?.message || error);
      });
    }
    setPermissionsPromptDismissed(true);
  };

  const showPermissionPrompt = !permissionsPromptDismissed && (
    permissionStatus.notifications === 'default'
    || permissionStatus.geolocation === 'prompt'
    || permissionStatus.microphone === 'prompt'
  );

  return {
    dismissPermissionsPrompt,
    gelezenNotificaties,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    notificationCount,
    notifications,
    permissionStatus,
    permissionsPromptDismissed,
    requestAppPermissions,
    requestingPermissions,
    refreshPermissionStatus,
    sendTestNotification,
    setGelezenNotificaties,
    setShowNotificationPopup,
    showNotificationPopup,
    showPermissionPrompt,
  };
}
